/**
 * EmojiPickerPopover — wraps `emoji-picker-react` for chat reactions.
 *
 * Phase 4c. Lazy-loaded via `React.lazy` so the picker JS bundle (~150KB
 * gzipped) is only fetched the first time the user opens the popover.
 * The chat composer's hand-rolled `<EmojiPicker>` (in `MarkdownEditor`)
 * stays — that surface inserts a literal emoji into the editor and
 * doesn't justify the extra bundle. Reactions are a richer surface
 * (search + categories + skin-tone) so we eat the cost there.
 *
 * Behavior:
 *   - On emoji select → call `onSelect(emoji)` with the unified emoji
 *     character, then close the popover.
 *   - Auto-close on outside click + Esc.
 *   - Positioned via `createPortal` to `document.body` + fixed
 *     coordinates derived from the trigger's bounding rect (mirrors the
 *     `MentionSuggestionPopover` pattern). Above-trigger preferred so
 *     the picker doesn't push timeline content out of view.
 */

import { useTheme } from '@/features/theme/use-theme'
import { getTabbables } from '@/lib/tabbable'
import { Suspense, lazy, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

// emoji-picker-react ships an event-payload type — we only need the
// `emoji` string. Avoid a top-level import so lazy() can split it.
interface EmojiPickerEvent {
  emoji: string
}

// React.lazy requires a default export — emoji-picker-react ships one,
// so the dynamic import resolves to its default-exported component.
const EmojiPicker = lazy(() => import('emoji-picker-react'))

interface EmojiPickerPopoverProps {
  /** Whether the popover is rendered. */
  open: boolean
  /** Bounding rect of the trigger button (anchor for positioning). */
  anchorRect: DOMRect | null
  onSelect: (emoji: string) => void
  onClose: () => void
  /**
   * When set, mousedowns on the trigger element don't fire the
   * outside-click close handler. Lets the trigger's own `onClick` toggle
   * the popover (open → close) without racing against the auto-close
   * (which would close the popover on mousedown, then the click would
   * re-open it).
   */
  triggerRef?: { current: HTMLElement | null }
  /**
   * Placement preference. `'above'` (default) is the chat-composer
   * convention — trigger sits at the bottom of the screen, picker pops
   * up above. `'below'` is the inline-caret convention used by the wiki
   * slash menu — the caret is mid-document and the picker should drop
   * below it so the user's eye doesn't have to jump. Either preference
   * flips when there isn't room in the preferred direction.
   */
  prefer?: 'above' | 'below'
  /**
   * Where focus goes when Tab / Shift+Tab leaves either end of the picker.
   * The popover is portalled to <body>, so without this the focused emoji
   * unmounts under the user and the next stop is the first tabbable in the
   * document. Defaults to the trigger when it is still mounted.
   */
  onTabOut?: () => void
}

const POPOVER_WIDTH = 320
const POPOVER_HEIGHT = 380

/**
 * `emoji-picker-react` ships its own light/dark palette, defaults to LIGHT,
 * and never consults `<html data-theme>` — so the picker rendered as a white
 * slab on Obsidian / Midnight. Two halves are needed:
 *
 *   1. `theme` — the library only swaps its `--epr-*` vars to the
 *      `--epr-dark-*` family when it is told the surface is dark. Its
 *      `'auto'` mode reads `prefers-color-scheme`, which is the OS
 *      preference, not the user's 3mote theme — the two disagree the moment
 *      someone picks Obsidian on a light-mode machine. So we map explicitly.
 *      Passed as the literal string the library's `Theme` enum resolves to;
 *      importing the enum would pull the package into the main bundle and
 *      defeat the `lazy()` split.
 *
 *   2. `DARK_THEME_VARS` — even in dark mode the library's own greys
 *      (`#222` ground, `#c0c0c0` text) are a third palette beside the two
 *      the app actually ships, so the picker read as a foreign panel
 *      floating over the theme. Re-pointing the `--epr-dark-*` family at our
 *      semantic tokens makes it a surface of whichever theme is applied,
 *      with no per-theme rule here.
 *
 * These must ride the picker's own `style` prop rather than a stylesheet
 * rule: the library declares the vars via a runtime-injected class ON the
 * picker root, and that injected CSS is UNLAYERED — so anything we write in
 * `global.css @layer components` would lose the cascade-layer comparison
 * before specificity is ever considered. The `style` prop lands as an inline
 * declaration on the same element, which wins outright.
 */
const DARK_THEME_VARS = {
  '--epr-dark-bg-color': 'var(--color-bg-elevated)',
  '--epr-dark-picker-border-color': 'var(--color-border)',
  '--epr-dark-text-color': 'var(--color-fg)',
  '--epr-dark-search-input-bg-color': 'var(--color-bg-muted)',
  '--epr-dark-search-input-bg-color-active': 'var(--color-bg-muted)',
  // Sticky category headers overlay the scrolling grid; the library's
  // default is translucent, which smears emoji through the label. Solid
  // `bg-elevated` matches the ground it sits on.
  '--epr-dark-category-label-bg-color': 'var(--color-bg-elevated)',
  '--epr-dark-hover-bg-color': 'var(--color-bg-muted)',
  '--epr-dark-hover-bg-color-reduced-opacity': 'var(--color-bg-muted)',
  '--epr-dark-focus-bg-color': 'var(--color-bg-muted)',
  '--epr-dark-emoji-variation-picker-bg-color': 'var(--color-bg-muted)',
  '--epr-dark-highlight-color': 'var(--color-accent)',
  '--epr-dark-category-icon-active-color': 'var(--color-accent)',
  '--epr-dark-skin-tone-picker-menu-color': 'var(--color-bg-elevated)',
  '--epr-dark-emoji-variation-indicator-color': 'var(--color-border-strong)',
} as const

/** The two app themes that render on a dark ground. */
const DARK_THEMES = new Set<string>(['obsidian', 'midnight'])

/**
 * The picker itself, split out so `useTheme()` runs ONLY while a picker is
 * actually open. `<EmojiPickerPopover>` is mounted per message row in the
 * chat timeline (and per comment, per reaction bar), so a hook at the top
 * level of that component would put a MutationObserver and a pair of query
 * subscriptions on every row — for a control that is open at most once in
 * the whole tab. Everything inside `<Suspense>` mounts only when the portal
 * renders, which is the one instance that needs to know the theme.
 */
function ThemedEmojiPicker({
  onSelect,
  onClose,
}: {
  onSelect: (emoji: string) => void
  onClose: () => void
}) {
  const { theme } = useTheme()
  const isDark = DARK_THEMES.has(theme)

  return (
    <EmojiPicker
      width={POPOVER_WIDTH}
      height={POPOVER_HEIGHT}
      onEmojiClick={(payload: EmojiPickerEvent) => {
        onSelect(payload.emoji)
        onClose()
      }}
      // Enable skin-tone selection (2026-05-15 — legacy parity
      // requirement dropped). The library shows a tone strip in
      // the picker footer + persists the user's choice in
      // `localStorage` automatically. Each tone variant is a
      // distinct Unicode codepoint, so `'👍🏽'` and `'👍'` count as
      // separate reactions on a message (Slack's behavior).
      //
      // `theme` + `style` carry the theming (see `DARK_THEME_VARS`). Only
      // the `--epr-dark-*` family is overridden, and it is inert until the
      // library applies its dark class — so the light themes keep the
      // vendor palette untouched.
      {...({
        skinTonesDisabled: false,
        lazyLoadEmojis: true,
        theme: isDark ? 'dark' : 'light',
        style: isDark ? DARK_THEME_VARS : undefined,
        // biome-ignore lint/suspicious/noExplicitAny: emoji-picker-react config types live in its default export and can't be referenced from the lazy import.
      } as any)}
    />
  )
}

/**
 * Resolve the popover's fixed-position coordinates given a placement
 * preference. Clamps to the viewport so it never spills off-screen on
 * narrow widths. The `prefer` argument biases the above-vs-below choice;
 * when the preferred side doesn't fit, the popover flips to the other
 * side rather than getting clamped to the viewport edge (which produces
 * a hover-far-from-the-anchor visual the wiki slash menu was hitting).
 */
function resolveAnchor(rect: DOMRect, prefer: 'above' | 'below'): { top: number; left: number } {
  const vw = window.innerWidth
  const vh = window.innerHeight
  const fitsAbove = rect.top >= POPOVER_HEIGHT + 8
  const fitsBelow = vh - rect.bottom >= POPOVER_HEIGHT + 8
  const placeAbove = prefer === 'above' ? fitsAbove : !fitsBelow && fitsAbove
  const top = placeAbove
    ? Math.max(8, rect.top - POPOVER_HEIGHT - 8)
    : Math.min(vh - POPOVER_HEIGHT - 8, rect.bottom + 8)
  // Right-align with the trigger by default, then clamp horizontally.
  const desiredLeft = rect.right - POPOVER_WIDTH
  const left = Math.max(8, Math.min(vw - POPOVER_WIDTH - 8, desiredLeft))
  return { top, left }
}

export function EmojiPickerPopover({
  open,
  anchorRect,
  onSelect,
  onClose,
  triggerRef,
  prefer = 'above',
  onTabOut,
}: EmojiPickerPopoverProps) {
  const { t } = useTranslation('chat')
  const popoverRef = useRef<HTMLDivElement | null>(null)
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null)

  // Resolve coords whenever the anchor rect changes.
  useEffect(() => {
    if (!open || !anchorRect) {
      setCoords(null)
      return
    }
    setCoords(resolveAnchor(anchorRect, prefer))
  }, [open, anchorRect, prefer])

  // Esc closes.
  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
      }
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [open, onClose])

  // Outside-click closes. Listen on `mousedown` so the trigger's onClick
  // (which calls `onClose` itself when re-clicked) doesn't race with our
  // outside-click and immediately reopen. Also exclude the trigger element
  // explicitly when `triggerRef` is set so re-clicking it can toggle the
  // popover closed (the trigger's own onClick handles the close path).
  useEffect(() => {
    if (!open) return
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node | null
      if (!target) return
      if (popoverRef.current?.contains(target)) return
      if (triggerRef?.current?.contains(target)) return
      onClose()
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [open, onClose, triggerRef])

  if (!open || !coords) return null

  return createPortal(
    <div
      ref={popoverRef}
      // biome-ignore lint/a11y/useSemanticElements: popover, not a true modal — same rationale as MarkdownEditor's emoji picker.
      role="dialog"
      aria-label={t('emojiPicker.ariaLabel')}
      className="fixed z-50 rounded-md border border-border bg-bg-elevated shadow-lg"
      onKeyDown={(event) => {
        if (event.key !== 'Tab') return
        const tabbables = getTabbables(event.currentTarget)
        const edge = event.shiftKey ? tabbables[0] : tabbables[tabbables.length - 1]
        if (document.activeElement !== edge) return
        event.preventDefault()
        onClose()
        if (onTabOut) onTabOut()
        else if (triggerRef?.current?.isConnected) triggerRef.current.focus()
      }}
      style={{
        top: coords.top,
        left: coords.left,
        width: POPOVER_WIDTH,
        height: POPOVER_HEIGHT,
      }}
    >
      <Suspense
        fallback={
          <div className="flex h-full w-full items-center justify-center text-sm text-fg-muted">
            {t('emojiPicker.loading')}
          </div>
        }
      >
        <ThemedEmojiPicker onSelect={onSelect} onClose={onClose} />
      </Suspense>
    </div>,
    document.body,
  )
}
